import React from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import moment from "moment";

const EtTrinityMorningHabitsChartDemo = () => {
  // Author - Atul Pandey
  // First Created - 16 Mar 2026
  // Purpose - lightweight sample chart to preview Morning Habits visualization in onboarding
  // Updated - 16 Mar 2026 - uses static sample data and mirrors actual chart style

  const allDatePoints = [
    { xValue: 0, xLabel: "Mon" },
    { xValue: 1, xLabel: "Tue" },
    { xValue: 2, xLabel: "Wed" },
    { xValue: 3, xLabel: "Thu" },
    { xValue: 4, xLabel: "Fri" },
    { xValue: 5, xLabel: "Sat" },
    { xValue: 6, xLabel: "Sun" },
  ];

  const habitSeries = [
    {
      habitId: "meditate",
      habitName: "Meditate",
      color: "#EA580C",
      points: [
        { xValue: 0, epochMinutes: 390 },
        { xValue: 1, epochMinutes: 405 },
        { xValue: 2, epochMinutes: 400 },
        { xValue: 3, epochMinutes: null },
        { xValue: 4, epochMinutes: 395 },
        { xValue: 5, epochMinutes: 420 },
        { xValue: 6, epochMinutes: 410 },
      ],
    },
    {
      habitId: "read",
      habitName: "Read",
      color: "#2563EB",
      points: [
        { xValue: 0, epochMinutes: 430 },
        { xValue: 1, epochMinutes: 440 },
        { xValue: 2, epochMinutes: null },
        { xValue: 3, epochMinutes: 445 },
        { xValue: 4, epochMinutes: 450 },
        { xValue: 5, epochMinutes: 460 },
        { xValue: 6, epochMinutes: 455 },
      ],
    },
    {
      habitId: "exercise",
      habitName: "Exercise",
      color: "#16A34A",
      points: [
        { xValue: 0, epochMinutes: null },
        { xValue: 1, epochMinutes: 360 },
        { xValue: 2, epochMinutes: 370 },
        { xValue: 3, epochMinutes: 365 },
        { xValue: 4, epochMinutes: 375 },
        { xValue: 5, epochMinutes: null },
        { xValue: 6, epochMinutes: 390 },
      ],
    },
  ];

  return (
    <div className="mt-3 rounded border border-gray-200 bg-white p-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">
          Sample Chart
        </p>
        <p className="text-xs text-gray-500">Morning habits · Mon to Sun</p>
      </div>

      <div className="w-full h-[260px] text-xs">
        <ResponsiveContainer>
          <ComposedChart
            data={allDatePoints}
            margin={{ top: 20, right: 20, bottom: 10, left: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" />

            <XAxis
              dataKey="xValue"
              type="number"
              domain={["dataMin", "dataMax"]}
              ticks={allDatePoints.map((d) => d.xValue)}
              tickFormatter={(x) => {
                const item = allDatePoints.find((d) => d.xValue === x);
                return item?.xLabel || "";
              }}
            />

            <YAxis
              type="number"
              domain={[300, 540]}
              ticks={[300, 360, 420, 480, 540]}
              tickFormatter={(m) =>
                moment().startOf("day").add(m, "minutes").format("h A")
              }
              label={{
                value: "Time",
                angle: -90,
                position: "insideLeft",
                offset: -2,
              }}
            />

            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload || payload.length === 0) return null;

                const dateLabel =
                  allDatePoints.find((d) => d.xValue === label)?.xLabel || label;

                return (
                  <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
                    <p className="font-semibold text-gray-700 mb-2">{dateLabel}</p>
                    {payload.map((entry, index) => {
                      if (entry.value === undefined || entry.value === null) return null;

                      const timeFormatted = moment()
                        .startOf("day")
                        .add(entry.value, "minutes")
                        .format("h:mm A");

                      return (
                        <p
                          key={index}
                          className="text-sm"
                          style={{ color: entry.color }}
                        >
                          {entry.name}: {timeFormatted}
                        </p>
                      );
                    })}
                  </div>
                );
              }}
            />

            <Legend wrapperStyle={{ paddingTop: 12 }} />

            {habitSeries.map((series) => (
              <Line
                key={series.habitId}
                type="monotone"
                data={series.points}
                dataKey="epochMinutes"
                xAxisId={0}
                yAxisId={0}
                stroke={series.color}
                strokeWidth={2}
                dot={{ r: 4, fill: series.color }}
                name={series.habitName}
                connectNulls={true}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <p className="text-xs text-gray-500 mt-2">
        This sample shows how your actual Morning Habits chart will begin to look as you build consistency through the week.
      </p>
    </div>
  );
};

export default EtTrinityMorningHabitsChartDemo;